import { useState, type ReactNode } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown, ChevronUp, ChevronsUpDown, Download } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { cn } from "@/lib/utils";
import type { PagedQueryResult } from "./usePagedQuery";
import { exportRowsToCsv } from "./export";

export interface RowAction<T> {
  label: string;
  icon?: React.ElementType;
  onClick?: (row: T) => void;
  href?: (row: T) => string;
  variant?: "default" | "destructive";
  hidden?: (row: T) => boolean;
  /** Mostra um AlertDialog antes de chamar onClick — substitui o confirm() nativo do legado. */
  confirm?: { title: string; description?: (row: T) => string; confirmLabel?: string };
}

export interface DataTableProps<T, F> {
  query: PagedQueryResult<T, F>;
  columns: ColumnDef<T, unknown>[];
  getRowId: (row: T) => string | number;
  actions?: RowAction<T>[];
  /** O formulário de filtro — recebe query.draft/setDraft/search de quem monta a tela. */
  toolbar?: ReactNode;
  emptyMessage?: string;
  exportFilename?: string;
  manualSorting?: boolean;
}

export function DataTable<T, F>({
  query,
  columns,
  getRowId,
  actions,
  toolbar,
  emptyMessage = "Nenhum registro encontrado.",
  exportFilename,
  manualSorting = true,
}: DataTableProps<T, F>) {
  const [pending, setPending] = useState<{ action: RowAction<T>; row: T } | null>(null);

  const allColumns: ColumnDef<T, unknown>[] = actions?.length
    ? [
        ...columns,
        {
          id: "__actions",
          header: "Ações",
          cell: ({ row }) => (
            <div className="flex items-center gap-1">
              {actions
                .filter((a) => !a.hidden?.(row.original))
                .map((a) => {
                  const Icon = a.icon;
                  const content = (
                    <>
                      {Icon && <Icon className="h-4 w-4" />}
                      <span className="sr-only">{a.label}</span>
                    </>
                  );
                  const className = cn(
                    "inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-muted",
                    a.variant === "destructive" && "text-destructive hover:bg-destructive/10",
                  );
                  if (a.href) {
                    return (
                      <a key={a.label} href={a.href(row.original)} title={a.label} className={className}>
                        {content}
                      </a>
                    );
                  }
                  return (
                    <button
                      key={a.label}
                      type="button"
                      title={a.label}
                      onClick={() => (a.confirm ? setPending({ action: a, row: row.original }) : a.onClick?.(row.original))}
                      className={className}
                    >
                      {content}
                    </button>
                  );
                })}
            </div>
          ),
        },
      ]
    : columns;

  const table = useReactTable({
    data: query.rows,
    columns: allColumns,
    getRowId: (row) => String(getRowId(row)),
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting,
    manualFiltering: true,
    pageCount: query.page?.totalPages ?? -1,
  });

  const handleSort = (columnId: string, enableSorting: boolean | undefined) => {
    if (!manualSorting || enableSorting === false) return;
    const prev = query.sort;
    if (!prev || prev.field !== columnId) {
      query.setSort({ field: columnId, direction: "ASC" });
    } else if (prev.direction === "ASC") {
      query.setSort({ field: columnId, direction: "DESC" });
    } else {
      query.setSort(undefined);
    }
  };

  const totalElements = query.page?.totalElements ?? 0;
  const from = totalElements === 0 ? 0 : query.pageIndex * query.pageSize + 1;
  const to = Math.min(totalElements, (query.pageIndex + 1) * query.pageSize);
  const lastPage = query.page ? query.page.totalPages - 1 : 0;

  return (
    <div className="space-y-4">
      {toolbar && <div className="rounded-xl border border-border bg-card p-4 shadow-card">{toolbar}</div>}

      <div className="rounded-xl border border-border bg-card shadow-card">
        {query.isFetching && !query.isLoading && (
          <div className="h-0.5 w-full overflow-hidden bg-muted">
            <div className="h-full w-1/3 animate-pulse bg-primary" />
          </div>
        )}

        {exportFilename && (
          <div className="flex justify-end border-b border-border p-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => exportRowsToCsv(exportFilename, columns, query.rows)}
              disabled={query.rows.length === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              Exportar CSV (página atual)
            </Button>
          </div>
        )}

        <div className={cn("overflow-x-auto", query.isFetching && !query.isLoading && "opacity-60 pointer-events-none")}>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const sortable = manualSorting && header.column.columnDef.enableSorting !== false && header.id !== "__actions";
                    const active = query.sort?.field === header.id;
                    return (
                      <TableHead
                        key={header.id}
                        className={cn(sortable && "cursor-pointer select-none")}
                        onClick={() => sortable && handleSort(header.id, header.column.columnDef.enableSorting)}
                      >
                        <div className="flex items-center gap-1">
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          {sortable &&
                            (active ? (
                              query.sort?.direction === "ASC" ? (
                                <ChevronUp className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronDown className="h-3.5 w-3.5" />
                              )
                            ) : (
                              <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground/50" />
                            ))}
                        </div>
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {query.isLoading ? (
                Array.from({ length: query.pageSize }).map((_, i) => (
                  <TableRow key={i}>
                    {allColumns.map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-5 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : query.isError ? (
                <TableRow>
                  <TableCell colSpan={allColumns.length}>
                    <Alert variant="destructive" className="my-2">
                      <AlertTitle>Não foi possível carregar os dados.</AlertTitle>
                      <AlertDescription>
                        <Button variant="outline" size="sm" className="mt-2" onClick={query.refetch}>
                          Tentar novamente
                        </Button>
                      </AlertDescription>
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={allColumns.length} className="py-10 text-center text-sm text-muted-foreground">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-col gap-3 border-t border-border p-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Exibindo</span>
            <Select value={String(query.pageSize)} onValueChange={(v) => query.setPageSize(Number(v))}>
              <SelectTrigger className="h-8 w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {query.pageSizeOptions.map((opt) => (
                  <SelectItem key={opt} value={String(opt)}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span>
              de {totalElements} ({totalElements === 0 ? "0" : `${from}-${to}`})
            </span>
          </div>

          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={query.pageIndex === 0} onClick={() => query.setPageIndex(0)}>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={query.pageIndex === 0}
              onClick={() => query.setPageIndex(query.pageIndex - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-2 text-sm text-muted-foreground">
              Página {query.pageIndex + 1} de {Math.max(query.page?.totalPages ?? 1, 1)}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={query.page?.last ?? true}
              onClick={() => query.setPageIndex(query.pageIndex + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={query.page?.last ?? true}
              onClick={() => query.setPageIndex(lastPage)}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!pending}
        onOpenChange={(open) => !open && setPending(null)}
        title={pending?.action.confirm?.title ?? ""}
        description={pending?.action.confirm?.description?.(pending.row)}
        confirmLabel={pending?.action.confirm?.confirmLabel}
        onConfirm={() => {
          if (pending) {
            pending.action.onClick?.(pending.row);
            setPending(null);
          }
        }}
      />
    </div>
  );
}
